import { useLaborRateProfiles, useMarkupProfiles } from './useDynamicPricing';
import { rollUpService, type ServicePart } from '@/lib/sales/service-rollup';
import type { DynamicServicePricingConfig, ServiceComponent } from '@/types/pricebook';
import {
  componentSellingUnitPrice,
  type PricebookComponentItemCache,
} from '@/components/organisms/pricebook/support/pricebook-bundle';

const round = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function useDynamicServiceSummary(
  config: DynamicServicePricingConfig,
  components: ServiceComponent[],
  items: PricebookComponentItemCache,
) {
  const { data: laborProfiles = [] } = useLaborRateProfiles();
  const { data: markupProfiles = [], isFetched: markupProfilesLoaded } = useMarkupProfiles();

  const labor = (config.laborLines ?? []).reduce((total, line) => {
    if (line.rateSource === 'FIXED')
      return total + (line.durationMinutes / 60) * (line.fixedRate ?? 0);
    const profile = laborProfiles.find((candidate) => candidate.id === line.laborRateProfileId);
    const amount = (profile?.tiers ?? []).reduce((sum, tier) => {
      const upper = tier.toMinute ?? line.durationMinutes;
      const used = Math.max(Math.min(line.durationMinutes, upper) - tier.fromMinute, 0);
      return sum + (used / 60) * tier.rate;
    }, 0);
    return total + amount;
  }, 0);

  let materialCost = 0;
  let equipmentCost = 0;
  const parts: ServicePart[] = [];
  for (const component of components) {
    // An optional part is not in the service's price until someone selects it
    // on a document, and the server's preview says exactly that
    // (`dynamic-pricing.service.ts` filters `isOptional` before it prices a
    // single row). Counting it here quoted the operator a price — and a cost —
    // that apply-price would never write.
    if (component.isOptional) continue;
    const item = items[component.itemId];
    const cost = Number(item?.cost ?? item?.costBasis ?? 0);
    // Split so the summary can show the same Labor / Material / Equipment
    // breakdown the estimate does, rather than one lumped "parts" figure.
    if (item?.type === 'equipment') equipmentCost += cost * component.quantity;
    else materialCost += cost * component.quantity;

    // Deliberately the SAME resolver the flat summary uses. The two paths
    // answer one question — what is this part worth to the service — and each
    // having its own arithmetic is how the flat path came to value parts at
    // catalog price while its own controls reported 0% markup.
    const unitPrice = componentSellingUnitPrice(component, item, {
      markupProfiles,
      markupProfilesLoaded,
      materialMarkupProfileId: config.materialMarkupProfileId,
      equipmentMarkupProfileId: config.equipmentMarkupProfileId,
      useSameMarkupProfile: config.useSameMarkupProfile,
    });
    parts.push({
      type: item?.type === 'equipment' ? 'EQUIPMENT' : 'MATERIAL',
      costBasis: cost,
      basePrice: unitPrice,
      quantity: component.quantity,
      // A part the service does not bill separately still contributes its cost
      // AND its markup to what the service sells for — the two questions are
      // independent. What the treatment decides is only whether the part also
      // charges the customer on a row of its own.
      pricingTreatment: component.pricingTreatment,
    });
  }

  // Complexity is a percentage of labor and parts, so it has to be resolved
  // against an unadjusted roll-up before the real one can be built.
  const unadjusted = rollUpService({ labor, parts });
  const complexity =
    (unadjusted.preSurcharge * Math.max(config.complexityAdjustmentPercent ?? 0, 0)) / 100;
  // The surcharge — what the pricebook UI calls the profit uplift — is charged
  // on labor and parts and never compounds onto the complexity adjustment, so
  // the roll-up is handed complexity as a figure that is already resolved. Its
  // stored percentage is canonical markup, which is the form the roll-up
  // charges.
  const rollup = rollUpService({
    labor,
    parts,
    otherModifiers: complexity,
    surcharge: config.surcharge,
  });
  const surchargeAppliesToLabor = config.surcharge?.appliesTo
    ? config.surcharge.appliesTo.labor === true
    : true;
  const laborMarkupPercent =
    config.surcharge?.type === 'PERCENT' && surchargeAppliesToLabor
      ? round(config.surcharge.value)
      : null;
  // Labor is a cost, not pure revenue: the rate profile states what an hour
  // costs and markup is added on top. Leaving it out reported a service as
  // costing only its parts, which made every job look more profitable than it
  // is — and disagreed with the estimate, which counts the same hours as cost.
  const calculatedCost = labor + rollup.partsCost;
  return {
    labor: round(labor),
    parts: rollup.partsSellingContribution,
    calculatedCost: round(calculatedCost),
    laborCost: round(labor),
    materialCost: round(materialCost),
    equipmentCost: round(equipmentCost),
    partsCost: rollup.partsCost,
    complexity: round(complexity),
    surcharge: rollup.surcharge,
    /** Percentage added on labor; the dollar result is `surcharge`. */
    laborMarkupPercent,
    /** What the surcharge was charged on, so the figure can be explained. */
    surchargeBase: rollup.surchargeBase,
    calculatedPrice: rollup.total,
    /**
     * Of `calculatedPrice`, the amount belonging to parts that bill on their
     * own document line. Materializing this service has to take it off the
     * service line, or those parts are charged twice.
     */
    partsSeparatelyCharged: rollup.partsSeparatelyCharged,
    // Markup on the PARTS, measured against what the parts cost — labor is not
    // marked up here, so folding it into the base would dilute the figure.
    aggregateMarkup:
      rollup.partsCost > 0 ? round((rollup.partsMarkup / rollup.partsCost) * 100) : null,
    totalMinutes: (config.laborLines ?? []).reduce((sum, line) => sum + line.durationMinutes, 0),
  };
}
